import React from 'react';
import { isAfter, startOfToday, startOfYesterday, startOfWeek, startOfMonth } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import memoizeOne, { type MemoizedFn } from 'memoize-one';
import type { Locale } from '@jira/platform__supported-locales/src/supported-locales.tsx';
import BoardIcon from '@atlaskit/icon/core/board';
import DashboardIcon from '@atlaskit/icon/core/dashboard';
import FilterIcon from '@atlaskit/icon/core/filter';
import PagesIcon from '@atlaskit/icon/core/pages';
import RoadmapIcon from '@atlaskit/icon/core/roadmap';
import WorkItemIcon from '@atlaskit/icon/core/work-item';
import { ContainerAvatar } from '@atlaskit/navigation-system/side-nav-items/container-avatar';
import { formatDistanceStrictWithLocale } from '@atlassian/jira-platform-utils-date-fns/src/main.tsx';
import { softwarePlanTimelineRoute } from '@atlassian/jira-router-routes-advanced-roadmaps-routes/src/softwarePlanTimelineRoute.tsx';
import { generatePath } from '@atlassian/react-resource-router';
import {
	BOARD,
	DASHBOARD,
	FILTER,
	ISSUE,
	PLAN,
	PROJECT,
	QUEUE,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-recent/src/common/constants.tsx';

import type { ObjectListFlyoutHookResults } from '@atlassian/navigation-common/types';
import type { IntlShape } from '@atlassian/jira-intl';
import type { useRecentItemsPaged$data } from '../../../controllers/use-recent-items/__generated__/useRecentItemsPaged.graphql';
import { messages } from '../messages.tsx';
import type { JiraOLFItem, TimeRanges } from '../../types.tsx';

type NonNullableEdges = NonNullable<useRecentItemsPaged$data['jira']['_recentItems']>['edges'];
type Edge = NonNullable<NonNullableEdges>[number];
export type GQLJiraItem = NonNullable<NonNullable<Edge>['node']>;
type GroupedItems = Record<TimeRanges, GQLJiraItem[]>;

type TimeZone = string;
export const getSections: MemoizedFn<
	(
		items: GQLJiraItem[],
		timeZone: TimeZone,
		deps: Deps,
	) => ObjectListFlyoutHookResults<JiraOLFItem>['sections']
> = memoizeOne(getSectionItems);

function getSectionItems(
	items: GQLJiraItem[],
	timeZone: TimeZone,
	deps: Deps,
): ObjectListFlyoutHookResults<JiraOLFItem>['sections'] {
	const now = getCurrentTime();

	const groups = items.reduce<GroupedItems>(
		(acc, item) => {
			const viewTimeAgo = getLastViewedTimestamp(item);

			if (viewTimeAgo) {
				const timeAccessed = new Date(viewTimeAgo);
				const dateObj = utcToZonedTime(timeAccessed, timeZone);

				if (isAfter(dateObj, startOfToday())) {
					acc.today.push(item);
				} else if (isAfter(dateObj, startOfYesterday())) {
					acc.yesterday.push(item);
				} else if (isAfter(dateObj, startOfWeek(now))) {
					acc.thisWeek.push(item);
				} else if (isAfter(dateObj, startOfMonth(now))) {
					acc.thisMonth.push(item);
				} else {
					acc.moreThanAMonth.push(item);
				}
			}

			return acc;
		},
		{
			today: [],
			yesterday: [],
			thisWeek: [],
			thisMonth: [],
			moreThanAMonth: [],
		},
	);

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return (Object.entries(groups) as unknown as [TimeRanges, GQLJiraItem[]][]).map(
		([key, value]) => ({
			header: deps.formatMessage(messages[key]),
			items: value.map((i) => toOLFItem(i, key, deps)).filter(Boolean),
		}),
	);
}

export function getCurrentTime(): number {
	return Date.now();
}

function getLastViewedTimestamp(item: GQLJiraItem) {
	switch (item.__typename) {
		case 'JiraIssue':
		case 'JiraProject':
		case 'JiraPlan':
		case 'JiraBoard':
		case 'JiraDashboard':
		case 'JiraSystemFilter':
		case 'JiraCustomFilter':
		case 'JiraServiceManagementQueue':
			return item.lastViewedTimestamp;
		default:
			return null;
	}
}

function toOLFItem(item: GQLJiraItem, timeRange: TimeRanges, deps: Deps): JiraOLFItem | undefined {
	switch (item.__typename) {
		case 'JiraBoard': {
			const isLocationless = item.boardUrl?.includes('locationless=true');

			return isLocationless
				? undefined
				: {
						elemBefore: <BoardIcon spacing="spacious" color="currentColor" label="" />,
						href: item.boardUrl ?? '',
						primaryLabel: String(item.name ?? ''),
						secondaryLabel: getLastViewedDescription({
							lastViewedTimestamp: item.lastViewedTimestamp,
							...deps,
						}),
						analytics: { itemId: String(item?.boardId), itemType: BOARD, timeRange },
					};
		}

		case 'JiraDashboard':
			return {
				elemBefore: <DashboardIcon spacing="spacious" color="currentColor" label="" />,
				href: item.dashboardUrl ?? '',
				primaryLabel: String(item.dashboardTitle ?? ''),
				secondaryLabel: getLastViewedDescription({
					lastViewedTimestamp: item.lastViewedTimestamp,
					...deps,
				}),
				analytics: { itemId: String(item?.dashboardId), itemType: DASHBOARD, timeRange },
			};

		case 'JiraIssue':
			return {
				elemBefore: <WorkItemIcon spacing="spacious" color="currentColor" label="" />,
				href: item.issueWebUrl ?? '',
				primaryLabel: String(item.summaryField?.text ?? ''),
				secondaryLabel: getLastViewedDescription({
					lastViewedTimestamp: item.lastViewedTimestamp,
					...deps,
				}),
				analytics: { itemId: String(item?.issueId), itemType: ISSUE, timeRange },
			};

		case 'JiraPlan': {
			const planId = item.planId;
			const scenarioId = item.scenario?.scenarioId ?? '';
			const planUrl = generatePath(softwarePlanTimelineRoute.path, { planId, scenarioId });

			return {
				elemBefore: <RoadmapIcon spacing="spacious" color="currentColor" label="" />,
				href: planUrl,
				primaryLabel: String(item.planTitle ?? ''),
				secondaryLabel: getLastViewedDescription({
					lastViewedTimestamp: item.lastViewedTimestamp,
					...deps,
				}),
				analytics: { itemId: String(item?.planId), itemType: PLAN, timeRange },
			};
		}

		case 'JiraProject':
			return {
				elemBefore: <ContainerAvatar src={item.avatar?.large ?? ''} />,
				href: item.defaultNavigationItem?.url ?? `/browse/${item.key}`,
				primaryLabel: item.title,
				secondaryLabel: getLastViewedDescription({
					lastViewedTimestamp: item.lastViewedTimestamp,
					...deps,
				}),
				analytics: { itemId: String(item?.projectId), itemType: PROJECT, timeRange },
			};

		case 'JiraSystemFilter':
		case 'JiraCustomFilter':
			return {
				elemBefore: <FilterIcon spacing="spacious" color="currentColor" label="" />,
				href: item.filterUrl ?? '',
				primaryLabel: item.filterName,
				secondaryLabel: getLastViewedDescription({
					lastViewedTimestamp: item.lastViewedTimestamp,
					...deps,
				}),
				analytics: { itemId: String(item?.filterId), itemType: FILTER, timeRange },
			};

		case 'JiraServiceManagementQueue':
			return {
				elemBefore: <PagesIcon spacing="spacious" color="currentColor" label="" />,
				href: item.queueUrl ?? '',
				primaryLabel: item.queueTitle,
				secondaryLabel: getLastViewedDescription({
					lastViewedTimestamp: item.lastViewedTimestamp,
					...deps,
				}),
				analytics: { itemId: String(item?.queueId), itemType: QUEUE, timeRange },
			};
		default:
			return undefined;
	}
}

type Deps = {
	formatMessage: IntlShape['formatMessage'];
	locale: Locale;
};

type GetLastViewedDescriptionParams = Deps & {
	lastViewedTimestamp: number | null | undefined;
	description?: string;
};

// Non-hook version of src/packages/navigation-apps/sidebar-nav4/sidebars/content/recent/src/common/controllers/index.tsx
// Exported for testing
export function getLastViewedDescription(params: GetLastViewedDescriptionParams): string {
	const { description, formatMessage, lastViewedTimestamp, locale } = params;
	const now = getCurrentTime();

	const lastViewedTimeText = lastViewedTimestamp
		? formatMessage(messages.timeAgo, {
				timeAgo: formatDistanceStrictWithLocale(lastViewedTimestamp, now, locale),
			})
		: '';
	if ((description?.length ?? 0) > 0 && lastViewedTimeText.length > 0) {
		return `${description} \u2022 ${lastViewedTimeText}`;
	}
	return lastViewedTimeText;
}
